/**
 * Google PageSpeed Insights API Service
 * Free API - no auth required for basic usage
 * Rate limit: 25,000 requests/day
 */

export interface PageSpeedScore {
  performance: number;
  accessibility: number;
  bestPractices: number;
  seo: number;
}

export interface PageSpeedMetrics {
  firstContentfulPaint: string;
  largestContentfulPaint: string;
  speedIndex: string;
  timeToInteractive: string;
  totalBlockingTime: string;
  cumulativeLayoutShift: string;
}

export interface PageSpeedResult {
  scores: PageSpeedScore;
  metrics: PageSpeedMetrics;
  fetchTime: Date;
  url: string;
}

/**
 * Fetches PageSpeed Insights data for a given URL
 * Uses backend proxy to keep API key secure
 * @param websiteUrl - URL to analyze
 * @param strategy - Mobile or desktop
 * @param backendUrl - Backend base URL (from config)
 */
export async function fetchPageSpeedData(
  websiteUrl: string,
  strategy: 'mobile' | 'desktop' = 'mobile',
  backendUrl?: string,
): Promise<PageSpeedResult> {
  // Use backend proxy - get backend URL from config or use relative path
  // Relative path works when frontend and backend are on same domain
  const baseUrl = backendUrl || '';
  const url = new URL('/api/pagespeed', baseUrl || window.location.origin);
  url.searchParams.append('url', websiteUrl);
  url.searchParams.append('strategy', strategy);
  
  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error(`PageSpeed API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  // Extract scores (0-1 scale, convert to 0-100)
  const categories = data.lighthouseResult?.categories || {};
  const scores: PageSpeedScore = {
    performance: Math.round((categories.performance?.score || 0) * 100),
    accessibility: Math.round((categories.accessibility?.score || 0) * 100),
    bestPractices: Math.round((categories['best-practices']?.score || 0) * 100),
    seo: Math.round((categories.seo?.score || 0) * 100),
  };

  // Extract key metrics
  const audits = data.lighthouseResult?.audits || {};
  const metrics: PageSpeedMetrics = {
    firstContentfulPaint: audits['first-contentful-paint']?.displayValue || 'N/A',
    largestContentfulPaint: audits['largest-contentful-paint']?.displayValue || 'N/A',
    speedIndex: audits['speed-index']?.displayValue || 'N/A',
    timeToInteractive: audits.interactive?.displayValue || 'N/A',
    totalBlockingTime: audits['total-blocking-time']?.displayValue || 'N/A',
    cumulativeLayoutShift: audits['cumulative-layout-shift']?.displayValue || 'N/A',
  };

  return {
    scores,
    metrics,
    fetchTime: new Date(),
    url: websiteUrl,
  };
}

/**
 * Get score color based on Lighthouse scoring
 * 0-49: red (poor)
 * 50-89: orange (needs improvement)
 * 90-100: green (good)
 */
export function getScoreColor(score: number): 'error' | 'warning' | 'success' {
  if (score < 50) return 'error';
  if (score < 90) return 'warning';
  return 'success';
}

/**
 * Get score status text
 */
export function getScoreStatus(score: number): string {
  if (score < 50) return 'Poor';
  if (score < 90) return 'Needs Improvement';
  return 'Good';
}

